import styled, { css } from 'styled-components';

import { floorGameDuration as duration, floorGameEasing as easing, styledReset } from '@pkg/primitives';

import { tokens } from './DataTable.tokens';
import { bodyCellBase, cellBase, firstCellCorners, maskStyles } from './mixins.style';

interface StyledHeadHeaderProps {
    $sortOffset?: boolean;
}

export const Wrapper = styled.div`
    // Grid layout is necessary for horizontal scrolling.
    display: grid;
    min-height: 0;
`;

export const StyledDataTable = styled.div`
    ${styledReset.borderBox};

    --data-table-cell-padding: ${tokens.cell.padding}px;
    --data-table-cell-padding-small: calc(var(--data-table-cell-padding) - ${tokens.sortAction.spacing}px);
    --data-table-cell-padding-big: calc(var(--data-table-cell-padding) + ${tokens.sortAction.spacing}px);

    --data-table-thead-height: ${tokens.thead.height}px;

    --data-table-bg-color: rgb(255, 255, 255, 0.06);
    --data-table-highlight-color: rgb(255, 255, 255, 0.1);

    --mask-radius: ${tokens.table.radius}px;
    --mask-radius-blur: ${tokens.table.radius + 1}px;
    --mask-circle-size: calc(var(--mask-radius-blur) * 2);
    --mask-vert-rect-start: calc(var(--data-table-thead-height) + var(--mask-radius));

    &[data-table-align='start'] {
        --data-table-head-header-padding-left: var(--data-table-cell-padding-small);
        --data-table-head-header-padding-right: var(--data-table-cell-padding);
    }

    &[data-table-align='end'] {
        --data-table-head-header-padding-left: var(--data-table-cell-padding);
        --data-table-head-header-padding-right: var(--data-table-cell-padding-small);
    }

    &[data-table-collapse='collapse'] {
        --data-table-border-collapse: collapse;
        ${maskStyles};
        overflow: auto;

        // We might find 'proximity' to feel better.
        scroll-snap-type: x mandatory;

        // Unfortunately, we cannot use "start align" snapping
        // because the first "sticky cell" width is dynamic.
        // This also means we cannot prescribe a left-offset...
        // however, we could reasonably predict this for a
        // "truncated Collection" variant:
        /*
        scroll-padding-left:
            calc(
                var(--data-table-cell-padding) +
                var(--data-table-cell-padding-big) +
                // tokens.collection.minWidth
                144px
            );
        */
    }

    &[data-table-collapse='separate'] {
        --data-table-border-collapse: separate;

        --data-table-cell-radius-top-left: ${tokens.table.radius}px;
        --data-table-cell-radius-bottom-left: ${tokens.table.radius}px;
        --data-table-cell-radius-top-right: ${tokens.table.radius}px;
        --data-table-cell-radius-bottom-right: ${tokens.table.radius}px;
    }
`;

export const Table = styled.table`
    ${styledReset.table};
    border-collapse: var(--data-table-border-collapse);
    border-spacing: 0 ${tokens.row.gap}px;
`;

export const TableRow = styled.tr`
    background-color: transparent;
`;

///
/// Table Head

export const TableHead = styled.thead``;

export const TableHeadHeader = styled.th<StyledHeadHeaderProps>`
    ${cellBase};
    height: var(--data-table-thead-height);

    ${({ $sortOffset }) =>
        $sortOffset &&
        css`
            padding-left: var(--data-table-cell-padding-small);

            &:last-child {
                padding-left: var(--data-table-head-header-padding-left);
                padding-right: var(--data-table-head-header-padding-right);
            }
        `};

    ${StyledDataTable}[data-table-collapse='collapse'] & {
        // TODO: Inorder for "sticky headers" to sticky to the top,
        // the <table /> needs to also be vertically scrollable.
        // We prefer to have the page be scrollable... so stickyness
        // will not work at the moment.
        z-index: 1;
        position: sticky;
        top: 0;
    }

    ${StyledDataTable}[data-table-collapse='collapse'] &:first-child {
        z-index: 2;
        left: 0;
    }
`;

export const TableHeadHeaderInterior = styled.div`
    display: grid;
    justify-content: var(--data-table-last-cell-align, start);
`;

///
/// Table Body

export const TableBody = styled.tbody`
    ${StyledDataTable}[data-table-collapse='collapse'] & {
        background-color: var(--data-table-bg-color);

        ${TableRow} {
            transition: background-color ${duration.normal}ms ${easing.cubic};

            &:hover {
                background-color: var(--data-table-highlight-color);
            }
        }
    }
`;

export const TableBodyHeader = styled.th`
    ${bodyCellBase};

    ${StyledDataTable}[data-table-collapse='collapse'] & {
        z-index: 2;
        position: sticky;
        left: 0;
    }

    ${StyledDataTable}[data-table-collapse='collapse'] ${TableRow}:hover &::before {
        background-color: var(--data-table-highlight-color);
    }

    ${StyledDataTable}[data-table-collapse='separate'] & {
        ${firstCellCorners};
    }
`;

export const TableBodyCell = styled.td`
    ${bodyCellBase};

    // The "empty state" cell requires aggresive
    // specificity in order to reset the required styles.
    &[colspan]:first-child:last-child:only-child {
        --data-table-last-cell-align: center;
        padding-right: var(--data-table-cell-padding);
        text-align: center;
    }

    ${StyledDataTable}[data-table-collapse='separate'] ${TableBody} &:first-child {
        ${firstCellCorners};
    }

    ${StyledDataTable}[data-table-collapse='separate'] ${TableBody} &:last-child {
        border-top-right-radius: var(--data-table-cell-radius-top-right);
        border-bottom-right-radius: var(--data-table-cell-radius-bottom-right);
    }
`;
